import { Chip, CircularProgress } from '@mui/material'
import CustomInput from '../../components/TextInput'
import CustomPaper from '../../containers/Paper'
import CustomButton from '../../components/Button'
import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppRoutes } from '../../helpers/routes'
import { RegisterContext } from '../../context/RegisterContext'
import CustomDatePicker from '../../components/DatePicker'
import AvatarUploader, { AvatarPlaceholder } from './avatarUploader/AvatarUploader'
import CustomModal from '../../containers/Modal'
import FormList from './FormList'
import { TEducationalInfo, TProfessionalInfo, TRegisterFullInfo } from '../../hooks/useFormRegister'
import { Country, RegisterFormInfoTypes } from '../../helpers/enums'
import AuthService from '../../services/AuthService'
import { useSnackbar } from 'notistack'
import CustomDropdown from '../../components/Dropdown'
import CustomRadioButton from '../../components/RadioButton'
import { UserDTO } from '../../models/users'
import { FetchResponse } from '../../models/shared'
import UserService from '../../services/UserService'
import 'react-phone-input-2/lib/style.css'
import CustomPhoneInput from '../../components/PhoneInput'
import useSignIn from 'react-auth-kit/hooks/useSignIn'
import { AuthenticatedUserData } from '../../models/auth'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import dayjs from 'dayjs'
import CustomIconButton from '../../components/IconButton'
import EditIcon from '@mui/icons-material/Edit'
import SearchAbilitiesModal from '../../components/Modal/SearchAbilitiesModal'
import { removeDuplicatesFromArray } from '../../helpers/utils'

const DEFAULT_ERRORS = {
    firstName: '',
    lastName: '',
    birthdate: '',
    phone: '',
    image: '',
}

interface Props {
    linkedInUser?: UserDTO
}
const UserForm = ({ linkedInUser }: Props) => {
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()
    const [pending, setPending] = useState<boolean>(false)
    const [errors, setErrors] = useState<typeof DEFAULT_ERRORS>(DEFAULT_ERRORS)
    const [isAvatarUploadOpen, setIsAvatarUploadOpen] = useState<boolean>(false)
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false)
    const [isPendingModalOpen, setIsPendingModalOpen] = useState<boolean>(false)
    const [isAbilitiesModalOpen, setIsAbilitiesModalOpen] = useState<boolean>(false)
    const { registerInfo, personalInfo, professionalInfo, educationalInfo, saveInfo } =
        useContext(RegisterContext)
    const signIn = useSignIn()

    useEffect(() => {
        if (linkedInUser) {
            const newInfo = {
                ...personalInfo,
                firstName: linkedInUser.firstName,
                lastName: linkedInUser.lastName,
                image: linkedInUser.picture,
            }
            saveInfo(RegisterFormInfoTypes.Personal, newInfo)
        }
    }, [linkedInUser, saveInfo, personalInfo])

    const validateInfo = () => {
        const newErrors = {
            ...errors,
            phone:
                personalInfo.phone.length < 6 || personalInfo.phone.length > 20
                    ? 'El teléfono debe tener entre 6 y 20 caracteres'
                    : '',
        }

        setErrors(newErrors)
        return Object.values(newErrors).every((x) => x === '')
    }

    const disableSubmit = () =>
        Object.entries(personalInfo).some(
            (x) => x[0] !== 'image' && x[0] !== 'abilities' && x[1] === '',
        ) ||
        professionalInfo.length === 0 ||
        educationalInfo.length === 0 ||
        [...personalInfo.abilities, ...getIndirectAbilities()].length === 0

    const handleOnChange = (name: string, value: string) => {
        const newInfo = { ...personalInfo, [name]: value }
        saveInfo(RegisterFormInfoTypes.Personal, newInfo)
    }

    const handleAbilityChange = (ability: string) => {
        const newInfo = {
            ...personalInfo,
            abilities: personalInfo.abilities.includes(ability)
                ? personalInfo.abilities.filter((x) => x !== ability)
                : [...personalInfo.abilities, ability],
        }
        saveInfo(RegisterFormInfoTypes.Personal, newInfo)
    }

    const handleSubmit = async () => {
        if (!validateInfo()) return
        let response: FetchResponse
        setPending(true)

        if (!linkedInUser) {
            response = await new AuthService().registerUser({
                registerInfo,
                personalInfo,
                professionalInfo,
                educationalInfo,
            } as TRegisterFullInfo)
        } else {
            response = await new UserService().updateUser({
                registerInfo,
                personalInfo,
                professionalInfo,
                educationalInfo,
            } as TRegisterFullInfo)
        }

        setPending(false)
        if (response.status !== 200) {
            enqueueSnackbar('Error en la solicitud.', {
                variant: 'error',
                autoHideDuration: 6000,
            })
        } else {
            if (!linkedInUser) setIsSuccessModalOpen(true)
            else {
                const userData: UserDTO = response.data
                if (!userData.pendingApprove) {
                    signIn({
                        auth: {
                            token: localStorage.getItem('linkedinToken') ?? '',
                            type: 'Bearer',
                        },
                        userState: {
                            picture: userData.picture,
                            fullName: userData.firstName + ' ' + userData.lastName,
                        } as AuthenticatedUserData,
                    })
                    localStorage.removeItem('linkedinToken')
                    navigate(AppRoutes.home)
                } else setIsPendingModalOpen(true)
            }
        }
    }

    const getIndirectAbilities = () => {
        const professional = professionalInfo.map((x) => x.abilities).flat()
        const educational = educationalInfo.map((x) => x.abilities).flat()
        return [...professional, ...educational]
    }

    return (
        <div className="w-full min-h-full flex justify-center items-center">
            <CustomPaper className="flex flex-col w-[350px] md:w-[50vw] py-3 md:py-6 text-primary overflow-auto max-h-[80vh] md:max-h-[90vh]">
                <span className="text-xl md:text-2xl">Registro de usuario</span>
                <span className="my-1 md:my-3 text-base md:text-xl">Información personal</span>
                <div className="h-[120px] md:h-[140px]">
                    <div className="flex items-center gap-2 md:gap-4 h-full">
                        <div
                            className={`ml-1 md:ml-2 h-4/5 md:h-full aspect-square rounded-full outline outline-primary -outline-offset-1 outline-[2px] ${!linkedInUser ? 'cursor-pointer' : ''}`}
                            onClick={() => {
                                if (!linkedInUser) setIsAvatarUploadOpen(true)
                            }}
                        >
                            {!!personalInfo.image ? (
                                <img
                                    width="100%"
                                    height="100%"
                                    alt="userImage"
                                    src={personalInfo.image}
                                    className="rounded-full"
                                />
                            ) : (
                                <AvatarPlaceholder />
                            )}
                        </div>
                        <div className="flex flex-col gap-2 md:gap-4 w-full">
                            <CustomInput
                                type="text"
                                label="Nombres"
                                name="firstName"
                                value={personalInfo.firstName}
                                onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                                disabled={Boolean(linkedInUser)}
                            />
                            <CustomInput
                                type="text"
                                label="Apellidos"
                                name="lastName"
                                value={personalInfo.lastName}
                                onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                                disabled={Boolean(linkedInUser)}
                            />
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mt-2 md:mt-3">
                    <CustomPhoneInput
                        value={personalInfo.phone}
                        onChange={(e) => handleOnChange('phone', e)}
                    />
                    <CustomDatePicker
                        label="Fecha de nacimiento"
                        name="birthdate"
                        value={personalInfo.birthdate}
                        onChange={handleOnChange}
                        maxValue={dayjs(Date.now())}
                    />
                    <CustomDropdown
                        label="Nacionalidad"
                        name="nationality"
                        data={Object.keys(Country)}
                        value={personalInfo.nationality}
                        onChange={(e) => handleOnChange(e.target.name, e.target.value as string)}
                    />
                    <CustomRadioButton
                        name="livesAbroad"
                        value={personalInfo.livesAbroad}
                        onChange={(e) =>
                            saveInfo(RegisterFormInfoTypes.Personal, {
                                ...personalInfo,
                                livesAbroad: e.target.value === 'true',
                            })
                        }
                        label="¿Vivís en el extranjero?"
                        className="ml-1 text-[#777777] text-sm md:text-base"
                    />
                </div>
                <FormList<TProfessionalInfo>
                    infoArray={professionalInfo}
                    title="Información Profesional"
                    type={RegisterFormInfoTypes.Professional}
                    dateRef={personalInfo.birthdate}
                />
                <FormList<TEducationalInfo>
                    infoArray={educationalInfo}
                    title="Educación"
                    type={RegisterFormInfoTypes.Educational}
                    dateRef={personalInfo.birthdate}
                />
                <div className="flex gap-1 md:gap-2 mt-2 md:mt-5 items-center">
                    <span className="text-base md:text-xl">Aptitudes</span>
                    <CustomIconButton
                        className="h-fit"
                        icon={<EditIcon />}
                        onClick={() => setIsAbilitiesModalOpen(true)}
                    />
                </div>
                {personalInfo.abilities.length > 0 || getIndirectAbilities().length > 0 ? (
                    <div className="flex gap-1 items-center my-2 flex-wrap">
                        {personalInfo.abilities.map((ability) => (
                            <div key={`selected${ability}`}>
                                <Chip
                                    label={ability}
                                    className="bg-primary text-white"
                                    sx={{
                                        '& .MuiChip-deleteIcon': {
                                            color: 'white',
                                        },
                                        '&:hover .MuiChip-deleteIcon': {
                                            color: 'white',
                                        },
                                    }}
                                />
                            </div>
                        ))}
                        {removeDuplicatesFromArray(
                            getIndirectAbilities().filter(
                                (x) => !personalInfo.abilities.includes(x as string),
                            ),
                        ).map((ability) => (
                            <div key={`selected${ability}`}>
                                <Chip
                                    label={ability}
                                    className="bg-disabled text-primary font-[500]"
                                    sx={{
                                        '& .MuiChip-deleteIcon': {
                                            color: 'white',
                                        },
                                        '&:hover .MuiChip-deleteIcon': {
                                            color: 'white',
                                        },
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    <span className="text-xs md:text-base">
                        Seleccioná las aptitudes o habilidades que creas que te identifican mejor.
                    </span>
                )}
                <CustomButton
                    className="w-full md:mt-6 h-[36px]"
                    onClick={handleSubmit}
                    disabled={disableSubmit() || pending}
                >
                    {!pending ? (
                        'Enviar'
                    ) : (
                        <CircularProgress
                            className="text-primary"
                            size={24}
                        />
                    )}
                </CustomButton>
            </CustomPaper>
            <CustomModal
                isOpen={isAvatarUploadOpen}
                handleClose={() => setIsAvatarUploadOpen(false)}
            >
                <AvatarUploader
                    handleClose={() => setIsAvatarUploadOpen(false)}
                    setAvatarImage={(image?: string) => handleOnChange('image', image!)}
                />
            </CustomModal>
            <CustomModal
                isOpen={isSuccessModalOpen}
                handleClose={() => setIsSuccessModalOpen(false)}
                onBackdropClick={() => navigate(AppRoutes.login)}
            >
                <div className="w-[70vw] md:w-[35vw] text-primary flex flex-col gap-3 md:gap-8">
                    <span className="w-full text-center text-xl md:text-4xl font-bold">
                        ¡Usuario creado correctamente!
                    </span>
                    <div className="flex gap-2 md:gap-6 mx-2 items-center justify-center">
                        <div className="flex gap-2 md:gap-6 flex-col">
                            <span className="text-base md:text-2xl font-semibold">
                                El usuario se creó correctamente y el siguiente paso es validar tu
                                email.
                            </span>
                            <span className="text-sm md:text-xl">
                                Revisá tu correo para continuar.
                            </span>
                        </div>
                        <CheckCircleOutlineIcon className="text-[75px] md:text-[200px] text-success" />
                    </div>
                    <CustomButton
                        text="Volver al inicio"
                        onClick={() => navigate(AppRoutes.login)}
                    />
                </div>
            </CustomModal>
            <CustomModal
                isOpen={isPendingModalOpen}
                handleClose={() => setIsPendingModalOpen(false)}
                onBackdropClick={() => navigate(AppRoutes.login)}
            >
                <div className="w-[70vw] md:w-[40vw] text-primary flex flex-col gap-3 md:gap-8">
                    <span className="w-full text-center text-xl md:text-4xl font-bold">
                        Usuario pendiente de aprobación
                    </span>
                    <div className="flex gap-2 md:gap-6 mx-2 items-center justify-center">
                        <div className="flex gap-2 md:gap-6 flex-col">
                            <span className="text-base md:text-2xl font-semibold">
                                El usuario se creó correctamente y ahora necesita ser aprobado por
                                un administrador.
                            </span>
                            <span className="text-sm md:text-xl">
                                Se te informará por correo cuando esto suceda.
                            </span>
                        </div>
                        <VerifiedUserIcon className="text-[75px] md:text-[200px] text-alert" />
                    </div>
                    <CustomButton
                        text="Volver al inicio"
                        onClick={() => navigate(AppRoutes.login)}
                    />
                </div>
            </CustomModal>
            <CustomModal
                isOpen={isAbilitiesModalOpen}
                handleClose={() => setIsAbilitiesModalOpen(false)}
            >
                <SearchAbilitiesModal
                    text="Seleccionar aptitudes"
                    handleClose={() => setIsAbilitiesModalOpen(false)}
                    selectedAbilities={personalInfo.abilities}
                    setSelectedAbility={handleAbilityChange}
                />
            </CustomModal>
        </div>
    )
}

export default UserForm
